import { Product } from "../../types";
import cornerLayersDecoration from "../../assets/images/corner-layers.png";
import { formatPrice, truncStr } from "../../helpers";
import CartButton from "../cart/CartButton";
import bottomReflection from "../../assets/images/bottom-reflection.png";

interface Props {
  className?: string;
  product: Product;
  currency: string;

  // UI & style controls
  highlighted?: boolean;
  disabled?: boolean; // disables interactions and dims the card
  showCartButton?: boolean; // toggle CartButton
  showPrice?: boolean; // toggle price display
  truncateName?: number; // max name length before truncation

  // Events
  onClick?: () => void;
  onAddToCart?: (product: Product) => void;
  onHover?: (product: Product) => void;

  // Formatting
  locale?: string; // e.g. "en-US", "de-DE"
}

const ProductCard = ({
  className = "",
  product,
  currency,
  highlighted,
  disabled,
  showCartButton = true,
  showPrice = true,
  truncateName = 35,
  locale = "en-US",
  onClick,
  onAddToCart,
  onHover,
}: Props) => {
  return (
    <div
      onClick={() => !disabled && onClick?.()}
      onMouseEnter={() => onHover?.(product)}
      className={`relative rounded-md overflow-hidden p-3 bg-gradient-to-b 
        from-white/5 to-white/10 min-w-[270px] min-h-[180px] flex flex-col 
        justify-between items-center 
        ${disabled ? "opacity-50 pointer-events-none" : ""} 
        ${className}`}
    >
      {/* Price */}
      {showPrice && (
        <div className="w-full flex items-center justify-end">
          <div
            className="relative bg-gradient-to-b from-[#47FF94]/40 px-3 py-1 
            rounded-sm to-[#285743]/70 border-[1px] border-[#47FF94]/20 border-b-0"
          >
            <p className="text-[#47FF94] text-[14px]">
              {formatPrice({ price: product.price, currency, locale })}
            </p>
          </div>
        </div>
      )}

      {/* Thumbnail */}
      <img
        src={product.previewImage}
        alt="preview"
        className="w-full max-w-[120px] my-4"
      />

      {/* Name & Cart button */}
      <div className="w-full">
        <p className="text-white text-[17px] text-center text-wrap w-full">
          {truncStr(product.name, truncateName)}
        </p>

        {showCartButton && (
          <CartButton
            product={product}
            className="mt-1"
            hideQuantity
            onClick={() => onAddToCart?.(product)}
          />
        )}
      </div>

      {/* Decorative top-left image */}
      <img
        src={cornerLayersDecoration}
        alt="decoration"
        className="absolute top-0 left-0"
      />

      {/* Border lines */}
      <div
        className="absolute top-0 left-0 right-0 h-[1.5px] 
        bg-gradient-to-r from-white/5 to-white/10"
      />
      <div
        className="absolute top-0 left-0 bottom-0 w-[1.8px] h-full 
        bg-gradient-to-b from-white/5 via-white/10 to-transparent"
      />
      <div
        className="absolute top-0 right-0 bottom-0 w-[1.8px] h-full 
        bg-gradient-to-b from-white/5 via-white/10 to-transparent"
      />

      {/* Bottom reflection (highlight effect) */}
      {highlighted && (
        <img
          src={bottomReflection}
          alt="reflection"
          className="absolute bottom-0 left-0 w-full h-full z-[-1]"
        />
      )}
    </div>
  );
};

export default ProductCard;
